import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';
import {
    HomeIcon,
    DocumentTextIcon,
    UsersIcon,
    PlusIcon,
    Bars3Icon,
    XMarkIcon,
    ArrowRightOnRectangleIcon,
    UserCircleIcon,
    ChevronRightIcon,
    ChatBubbleLeftRightIcon,
    PhoneIcon,
    SpeakerWaveIcon,
    Cog6ToothIcon,
    CreditCardIcon,
} from '@heroicons/react/24/outline';

const DashboardLayout = () => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const { user, logout, isAdmin } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();

    const handleLogout = () => {
        logout();
        toast.success('Logout successful');
        navigate('/login');
    };

    // Navigation items based on user role
    const navigationItems = [
        {
            name: 'Dashboard',
            href: '/dashboard',
            icon: HomeIcon,
            roles: ['admin', 'user'],
        },
        {
            name: 'Campaigns',
            href: '/campaigns',
            icon: DocumentTextIcon,
            roles: ['admin', 'user'],
        },
        {
            name: 'User Management',
            href: '/users',
            icon: UsersIcon,
            roles: ['admin'], // Only admins can manage users
        },
    ];

    const filteredNavigation = navigationItems.filter(item =>
        item.roles.includes(user?.role)
    );

    const isActive = (href) => {
        if (href === '/dashboard') {
            return location.pathname === href;
        }
        return location.pathname.startsWith(href);
    };

    const getPageTitle = () => {
        if (location.pathname === '/dashboard') return 'Dashboard';
        if (location.pathname === '/campaigns') return 'Campaigns';
        if (location.pathname === '/campaigns/new') return 'Create Campaign';
        if (location.pathname === '/campaigns/create') return 'Create Campaign';
        if (location.pathname === '/users') return 'User Management';
        if (location.pathname === '/profile') return 'Profile Settings';
        if (location.pathname.startsWith('/campaigns/') && location.pathname !== '/campaigns/new' && location.pathname !== '/campaigns/create') {
            return 'Campaign Details';
        }
        return 'Dashboard';
    };

    // Generate breadcrumb for current page
    const getBreadcrumb = () => {
        const pathParts = location.pathname.split('/').filter(Boolean);
        const breadcrumbItems = [
            { name: 'Dashboard', href: '/dashboard' }
        ];

        if (pathParts.length > 1) {
            if (pathParts[0] === 'campaigns') {
                breadcrumbItems.push({ name: 'Campaigns', href: '/campaigns' });
                if (pathParts[1] === 'new') {
                    breadcrumbItems.push({ name: 'Create Campaign', href: '/campaigns/new' });
                } else if (pathParts[1] === 'create') {
                    breadcrumbItems.push({ name: 'Create Campaign', href: '/campaigns/create' });
                } else if (pathParts.length > 1) {
                    breadcrumbItems.push({ name: 'Campaign Details', href: location.pathname });
                }
            } else if (pathParts[0] === 'users') {
                breadcrumbItems.push({ name: 'User Management', href: '/users' });
            } else if (pathParts[0] === 'profile') {
                breadcrumbItems.push({ name: 'Profile Settings', href: '/profile' });
            }
        }

        return breadcrumbItems;
    };

    return (
        <div className="h-screen bg-soft-50 lg:flex overflow-hidden">
            {/* Mobile sidebar backdrop */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 z-40 bg-soft-900/20 backdrop-blur-sm lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Sidebar - Fixed width and proper scrolling structure */}
            <div
                className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-soft-200/60 transform transition-all duration-300 ease-in-out lg:translate-x-0 lg:relative lg:flex lg:flex-shrink-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'
                    }`}
            >
                <div className="flex h-full flex-col justify-between">
                    <div>
                        {/* Logo/Header - Removed background color */}
                        <div className="flex-shrink-0 h-20 flex items-center justify-between px-4 bg-white border-b border-soft-200/60">
                            <div className="flex items-center space-x-3 w-full">
                                <div className="h-10 w-10 bg-primary-100 rounded-lg flex items-center justify-center shadow-sm border border-primary-200/50">
                                    <DocumentTextIcon className="h-6 w-6 text-primary-600" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <span className="text-lg font-bold text-soft-900 block leading-tight truncate">
                                        Campaign
                                    </span>
                                    <div className="text-xs text-soft-700 font-medium truncate">
                                        Management System
                                    </div>
                                </div>
                                <button
                                    onClick={() => setSidebarOpen(false)}
                                    className="lg:hidden text-soft-500 hover:text-soft-700 p-1.5 rounded-lg hover:bg-soft-100 transition-colors"
                                >
                                    <XMarkIcon className="h-5 w-5" />
                                </button>
                            </div>
                        </div>

                        {/* Navigation - Scrollable area when needed */}
                        <div className="overflow-y-auto">
                            <nav className="px-3 py-6 space-y-1">
                                <div className="text-xs font-semibold text-soft-500 uppercase tracking-wider mb-4 px-3">
                                    Main Navigation
                                </div>
                                {filteredNavigation.map((item) => (
                                    <Link
                                        key={item.name}
                                        to={item.href}
                                        onClick={() => setSidebarOpen(false)}
                                        className={`group flex items-center space-x-3 px-3 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${isActive(item.href)
                                            ? 'bg-primary-50 text-primary-700 shadow-sm border border-primary-200/50'
                                            : 'text-soft-600 hover:bg-soft-100 hover:text-soft-900'
                                            }`}
                                    >
                                        <div className={`p-2 rounded-lg transition-colors ${isActive(item.href)
                                            ? 'bg-primary-100 text-primary-600 shadow-sm'
                                            : 'bg-soft-200 text-soft-500 group-hover:bg-soft-300 group-hover:text-soft-600'
                                            }`}>
                                            <item.icon className="h-4 w-4" />
                                        </div>
                                        <span className="flex-1 truncate">{item.name}</span>
                                        {isActive(item.href) && (
                                            <div className="w-2 h-2 bg-primary-500 rounded-full shadow-sm"></div>
                                        )}
                                    </Link>
                                ))}
                            </nav>
                        </div>
                    </div>

                    {/* User info and logout button - Always fixed at bottom */}
                    <div className="flex-shrink-0 border-t border-soft-200/60 p-3 bg-soft-50/50">
                        <div className="flex items-center space-x-2 mb-3 p-2.5 rounded-xl bg-white shadow-sm border border-soft-200/60">
                            <div className="h-10 w-10 bg-gradient-to-r from-primary-400 to-secondary-400 rounded-lg flex items-center justify-center shadow-sm">
                                <UserCircleIcon className="h-6 w-6 text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-soft-900 truncate">
                                    {user?.name}
                                </p>
                                <p className="text-xs text-soft-600 truncate">
                                    {user?.email}
                                </p>
                                <span
                                    className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full mt-1 ${user?.role === 'admin'
                                        ? 'bg-secondary-100 text-secondary-700 border border-secondary-200/50'
                                        : 'bg-primary-100 text-primary-700 border border-primary-200/50'
                                        }`}
                                >
                                    {user?.role?.charAt(0).toUpperCase() + user?.role?.slice(1)}
                                </span>
                            </div>
                        </div>

                        {/* Profile Settings Button */}
                        <Link
                            to="/profile"
                            onClick={() => setSidebarOpen(false)}
                            className="w-full flex items-center justify-center space-x-2 px-3 py-2.5 text-sm font-medium text-soft-700 bg-white hover:bg-soft-50 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md border border-soft-200 mb-3"
                        >
                            <Cog6ToothIcon className="h-4 w-4" />
                            <span>Profile Settings</span>
                        </Link>

                        {/* Logout button - Always visible */}
                        <button
                            onClick={handleLogout}
                            className="w-full flex items-center justify-center space-x-2 px-3 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-error-500 to-error-600 hover:from-error-600 hover:to-error-700 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md border border-error-200"
                        >
                            <ArrowRightOnRectangleIcon className="h-4 w-4" />
                            <span>Sign Out</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Main content */}
            <div className="lg:flex-1 flex flex-col h-screen">
                {/* Enhanced Top Header - Removed duplicate user info and logout button */}
                <header className="flex-shrink-0 z-30 bg-white/95 backdrop-blur-lg shadow-sm border-b border-soft-200/60">
                    <div className="flex h-16 sm:h-20 items-center justify-between px-4 sm:px-6">
                        <div className="flex items-center space-x-2 sm:space-x-4 flex-1 min-w-0">
                            <button
                                onClick={() => setSidebarOpen(true)}
                                className="lg:hidden p-2 sm:p-2.5 text-soft-500 hover:text-soft-700 hover:bg-soft-100 rounded-xl transition-colors flex-shrink-0"
                            >
                                <Bars3Icon className="h-5 w-5 sm:h-6 sm:w-6" />
                            </button>
                            <div className="flex-1 min-w-0">
                                <h1 className="text-lg sm:text-2xl font-bold text-soft-900 truncate">
                                    {getPageTitle()}
                                </h1>
                                {/* Breadcrumb navigation with close sidebar functionality */}
                                <nav className="flex items-center space-x-1 mt-0.5 sm:mt-1">
                                    {getBreadcrumb().map((item, index) => (
                                        <React.Fragment key={item.href}>
                                            {index > 0 && (
                                                <ChevronRightIcon className="h-3 w-3 text-soft-400 flex-shrink-0" />
                                            )}
                                            <Link
                                                to={item.href}
                                                onClick={() => setSidebarOpen(false)}
                                                className="text-xs sm:text-sm text-soft-600 hover:text-primary-600 transition-colors truncate"
                                            >
                                                {item.name}
                                            </Link>
                                        </React.Fragment>
                                    ))}
                                </nav>
                            </div>
                        </div>

                        {/* Header actions - Simplified without duplicate user info */}
                        <div className="flex items-center space-x-2 sm:space-x-3 flex-shrink-0">
                            {/* Credits Display - Only for Users */}
                            {user?.role === 'user' && (
                                <div className="hidden sm:flex items-center space-x-2">
                                    {/* WhatsApp Credits */}
                                    <div
                                        className="flex items-center space-x-1 bg-green-50 text-green-700 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg border border-green-200 cursor-default"
                                        title="WhatsApp Credits"
                                    >
                                        <ChatBubbleLeftRightIcon className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                                        <span className="text-xs sm:text-sm font-medium">
                                            {user?.whatsappCredits?.toLocaleString() || 0}
                                        </span>
                                        <span className="text-xs text-green-600 hidden sm:inline">WA</span>
                                    </div>

                                    {/* SMS Credits */}
                                    <div
                                        className="flex items-center space-x-1 bg-blue-50 text-blue-700 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg border border-blue-200 cursor-default"
                                        title="SMS Credits"
                                    >
                                        <PhoneIcon className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                                        <span className="text-xs sm:text-sm font-medium">
                                            {user?.smsCredits?.toLocaleString() || 0}
                                        </span>
                                        <span className="text-xs text-blue-600 hidden sm:inline">SMS</span>
                                    </div>

                                    {/* Voice Credits */}
                                    <div
                                        className="flex items-center space-x-1 bg-purple-50 text-purple-700 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg border border-purple-200 cursor-default"
                                        title="Voice Call Credits"
                                    >
                                        <SpeakerWaveIcon className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                                        <span className="text-xs sm:text-sm font-medium">
                                            {user?.voiceCallCredits?.toLocaleString() || 0}
                                        </span>
                                        <span className="text-xs text-purple-600 hidden sm:inline">Voice</span>
                                    </div>
                                </div>
                            )}

                            {/* Mobile Credits Summary - Only for Users */}
                            {user?.role === 'user' && (
                                <div className="sm:hidden flex items-center space-x-1 bg-soft-100 px-2 py-1.5 rounded-lg border border-soft-200/50">
                                    <CreditCardIcon className="h-4 w-4 text-soft-600 flex-shrink-0" />
                                    <span className="text-xs font-medium text-soft-900">
                                        {((user?.whatsappCredits || 0) + (user?.smsCredits || 0) + (user?.voiceCallCredits || 0)).toLocaleString()}
                                    </span>
                                </div>
                            )}

                            {/* Quick user indicator */}
                            <div className="flex items-center space-x-2 bg-soft-100 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg border border-soft-200/50">
                                <div className="h-6 w-6 sm:h-8 sm:w-8 bg-gradient-to-r from-primary-400 to-secondary-400 rounded-lg flex items-center justify-center shadow-sm flex-shrink-0">
                                    <UserCircleIcon className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                                </div>
                                <div className="text-right hidden sm:block">
                                    <div className="text-sm font-semibold text-soft-900 truncate max-w-24">{user?.name}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </header>

                {/* Page content with proper spacing and scrolling */}
                <main className="flex-1 overflow-y-auto pt-4 sm:pt-6 px-4 sm:px-6 pb-6 sm:pb-8">
                    <div className="max-w-7xl mx-auto">
                        <Outlet />
                    </div>
                </main>
            </div>
        </div>
    );
};

export default DashboardLayout; 